import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ActionType,
  CustomStepCategoryEnum,
  DiscountDuration,
  DiscountSetting,
  DiscountTypeEnum,
  ExecutionTypeEnum,
  OfferIntents,
  StepDiscountSettingsLayout,
  StepDiscountSettingTypes,
  StepDurationUnits,
  StepElements,
  User,
} from '../../../_models';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormControlStatus } from '../../../_forms';
import { AlertService, StepEmulatorService, UserService } from '../../../_services';
import { HoldOffAbstractComponent } from './hold-off-abstract.component';
import { MatExpansionPanel } from '@angular/material';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

@Component({
  selector: 'step-discount-settings',
  templateUrl: './step-discount-settings.component.html',
  styleUrls: ['./step-discount-settings.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
      transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class StepDiscountSettingsComponent extends HoldOffAbstractComponent implements OnInit, OnChanges {
  @Input() layout: StepDiscountSettingsLayout;
  @Input() form: FormGroup;
  @Input() enableFormErrors: boolean;
  @Input() stepType: CustomStepCategoryEnum;
  @Input() isNew: boolean;
  @Input() templateId: string;
  @Input() offerIntent: OfferIntents = null;
  @Output() discountChange: EventEmitter<DiscountSetting> = new EventEmitter<DiscountSetting>();
  @Input() isSingleStepOffer: boolean = false;
  @Input() isReturnDownsell: boolean = false;
  stepElements = StepElements;
  discountDurations = DiscountDuration;
  discountStepType: StepDiscountSettingTypes;
  discountTypes = DiscountTypeEnum;
  executionTypes = ExecutionTypeEnum;
  stepDurationUnits = StepDurationUnits;
  inputTitle: string = 'Discount Amount';
  selectTitle: string = 'Discount if index not found';
  discountIndexList = [
    { value: 0, label: '1st Product Discount' },
    { value: 1, label: '2nd Product Discount' },
    { value: 2, label: '3rd Product Discount' },
    { value: 3, label: '4th Product Discount' },
    { value: 4, label: '5th Product Discount' },
  ];
  upsaleIndexList = [
    { value: 0, label: '1st Upsell Product' },
    { value: 1, label: '2nd Upsell Product' },
    { value: 2, label: '3rd Upsell Product' },
    { value: 3, label: '4th Upsell Product' },
    { value: 4, label: '5th Upsell Product' },
  ];
  actionList: FormArray;
  user: User = null;
  showDiscountWithRefund = false;

  @ViewChild('panel', { static: false }) panel: MatExpansionPanel;

  constructor(
    private stepEmulatorService: StepEmulatorService,
    private formBuilder: FormBuilder,
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    protected userService: UserService
  ) {
    super(alertService, router, location);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.offerIntent && !changes.offerIntent.firstChange) {
      if (this.isEligibleForDiscountWithRefund) {
        this.initializeDiscountWithRefund();
      }
    }

    if (changes.isReturnDownsell && !changes.isReturnDownsell.firstChange) {
      this.switchDiscountType();
    }
  }

  ngOnInit() {
    this.getCurrentUser();
    const inputs = this.form.get('inputs') as FormArray;
    const firstInput = inputs.at(0) as FormGroup;
    this.actionList = firstInput.get('actions') as FormArray;
    this.actions = this.actionList.at(0) as FormGroup;

    if (this.stepType === CustomStepCategoryEnum.DiscountFutureOrder && this.isEligibleForDiscountWithRefund) {
      this.initializeDiscountWithRefund();
    }

    this.discountStepType = this.layout.type;
    if (this.isDiscountRefund) {
      this.actions.patchValue({
        bill_now: false,
        final_billing_cycle_offset: null,
      });
    } else if (this.isFutureOrder) {
      this.actions.patchValue({
        include_tax: false,
      });
    }

    if (this.isReactivateSubscription) {
      const check_last_discount = this.actions.get('check_last_discount').value || false;
      const use_product_price = this.actions.get('use_product_price').value || false;

      this.actions.patchValue({ check_last_discount, use_product_price });

      if (this.isSingleStepOffer) {
        const discount_type = this.actions.get('discount_type').value;
        const discount = this.actions.get('discount').value || 20;
        if (discount_type != null) {
          this.actions.patchValue({ discount_type, discount });
        }
      }
    } else {
      super.ngOnInit();
    }

    this.switchDiscountType();
  }

  onDiscountChanged() {
    const discountType = this.actions.get('discount_type').value;
    let discount: DiscountSetting = { type: null, amount: 0, index: 0 };

    if (discountType != null) {
      const discountAmount = this.actions.get('discount').value || 0;
      const discountIndex = this.actions.get('discount_index').value || 0;
      discount = {
        type: parseInt(discountType, 10),
        amount: parseFloat(discountAmount),
        index: parseInt(discountIndex, 10),
      };
    }

    this.discountChange.emit(discount);
  }

  switchDiscountType() {
    const discountControl = this.actions.get('discount');

    if (discountControl) {
      const discount = discountControl.value;
      const discountType = this.actions.get('discount_type').value;

      if (discountType == null) {
        this.inputTitle = 'Discount';
        const discountTypeControl = this.actions.get('discount_type');
        discountControl.clearValidators();
        discountControl.updateValueAndValidity();
        discountTypeControl.clearValidators();
        discountTypeControl.updateValueAndValidity();
        this.actions.patchValue({ discount: null, discount_index: null });
      } else {
        switch (discountType) {
          case DiscountTypeEnum.Fixed:
            this.inputTitle = 'Fixed Amount';
            discountControl.setValidators([Validators.required, Validators.min(1)]);
            this.actions.patchValue({
              discount,
              discount_index: null,
            });
            break;

          case DiscountTypeEnum.Percent:
            this.inputTitle = `${this.isReturnDownsell ? 'Refund' : 'Discount'} Percentage`;
            discountControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);

            this.actions.patchValue({
              discount,
              discount_index: null,
            });
            break;

          case DiscountTypeEnum.PercentOfOrder:
            this.inputTitle = `${this.isReturnDownsell ? 'Refund' : 'Discount'} Percentage`;
            discountControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
            this.actions.patchValue({
              discount,
              discount_index: null,
              check_last_discount: false,
            });
            break;

          case DiscountTypeEnum.Index:
            this.inputTitle = 'Indexed Price Point';
            discountControl.setValidators([]);
            this.actions.patchValue({
              discount_index: 0,
            });
            break;

          case DiscountTypeEnum.IndexOrFixed:
            this.inputTitle = 'Indexed Price Point';
            discountControl.setValidators([Validators.required, Validators.min(1)]);
            this.selectTitle = 'Backup Fixed Amount';
            this.actions.patchValue({
              discount,
              discount_index: 0,
            });
            break;

          case DiscountTypeEnum.IndexOrPercent:
            this.inputTitle = 'Indexed Price Point';
            discountControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
            this.selectTitle = `Backup ${this.isReturnDownsell ? 'Refund' : 'Discount'} Percentage`;
            this.actions.patchValue({
              discount,
              discount_index: 0,
            });
            break;
        }
      }

      this.onDiscountChanged();
    }
  }

  toggleElement(element: StepElements) {
    this.stepEmulatorService.toggleElement(element);
  }

  isElementVisible(element: StepElements): boolean {
    return this.stepEmulatorService.isElementVisible(element);
  }

  getCurrentUser() {
    this.userService.getCurrent().subscribe(
      (user: User) => {
        this.user = user;
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  private initializeDiscountWithRefund() {
    if (this.stepType === CustomStepCategoryEnum.DiscountFutureOrder) {
      const discountIndex = this.actionList.value.findIndex((a) => a.resourcetype === ActionType.Discount);

      if (discountIndex > -1) {
        this.actions = this.actionList.at(discountIndex) as FormGroup;
      }

      const isDiscountWithRefund = this.actionList.value.findIndex((a) => a.resourcetype === ActionType.Refund) > -1;

      if (!this.actions.get('isDiscountWithRefund')) {
        this.actions.addControl('isDiscountWithRefund', this.formBuilder.control(isDiscountWithRefund));
      } else {
        this.actions.get('isDiscountWithRefund').setValue(isDiscountWithRefund);
      }

      this.showDiscountWithRefund = true;
    }
  }

  get isFixed() {
    return this.actions.get('discount_type').value === DiscountTypeEnum.Fixed;
  }
  get isPercent() {
    return this.actions.get('discount_type').value === DiscountTypeEnum.Percent;
  }
  get isIndex() {
    return this.actions.get('discount_type').value === DiscountTypeEnum.Index;
  }
  get isIndexOrFixed() {
    return this.actions.get('discount_type').value === DiscountTypeEnum.IndexOrFixed;
  }
  get isIndexOrPercent() {
    return this.actions.get('discount_type').value === DiscountTypeEnum.IndexOrPercent;
  }
  get isPercentOrder() {
    return this.actions.get('discount_type').value === DiscountTypeEnum.PercentOfOrder;
  }

  get isImmediate() {
    return this.actions.get('execution_type').value === ExecutionTypeEnum.Immediate;
  }
  get isDelayed() {
    return this.actions.get('execution_type').value === ExecutionTypeEnum.Delayed;
  }
  get isValidationOnly() {
    return this.actions.get('execution_type').value === ExecutionTypeEnum.ValidationOnly;
  }

  get isDiscountRefund() {
    return this.discountStepType === StepDiscountSettingTypes.DiscountRefund;
  }

  get isFutureOrder() {
    return this.discountStepType === StepDiscountSettingTypes.FutureOrder;
  }

  get isUpsellOrder() {
    return this.stepType === CustomStepCategoryEnum.UpsellOrder;
  }

  get isReactivateSubscription() {
    return this.stepType === CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount;
  }

  get isNoDiscount() {
    return this.isReactivateSubscription && this.actions.get('discount_type').value == null;
  }

  get settingsInvalidity() {
    return this.enableFormErrors && this.actions.status === FormControlStatus.Invalid;
  }

  get discountInvalidity() {
    return this.enableFormErrors && this.actions.controls.discount.status === FormControlStatus.Invalid;
  }

  get holdOffInvalidity() {
    return this.enableFormErrors && this.actions.controls.holdoff_delay.status === FormControlStatus.Invalid;
  }

  get isDiscountWithRefund() {
    return this.actions.get('isDiscountWithRefund') ? this.actions.get('isDiscountWithRefund').value : false;
  }

  get isEligibleForDiscountWithRefund() {
    return [OfferIntents.CancelSubscriptionHold, OfferIntents.CancelRebillHold].includes(this.offerIntent);
  }

  get showDiscountDuration() {
    return [
      OfferIntents.CancelSubscription,
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.ReactivateSubscription,
    ].includes(this.offerIntent);
  }
}
